import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Product } from "../../api/productService";
import { useCartStore } from "../../store/useStore";
import Container from "../Common/Container";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { useFeaturedProducts } from "../../hooks/useProducts";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1.5rem;
    margin: 1.5rem 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
    margin: 1rem 0;
  }
`;

const Card = styled.div`
  background: #f4efef;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(233, 125, 125, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(233, 125, 125, 0.1) 0%,
      rgba(233, 125, 125, 0.05) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 1rem;
  }

  &:hover {
    box-shadow: 0 8px 32px rgba(233, 125, 125, 0.15);
    border-color: #e97d7dff;
    transform: translateY(-4px) scale(1.02);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-2px) scale(0.98);
  }

  @media (max-width: 768px) {
    border-radius: 0.8rem;

    &:hover {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 480px) {
    border-radius: 0.6rem;

    &:hover {
      transform: none;
    }
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  transition: transform 0.3s ease;
  position: relative;
  z-index: 1;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const ProductTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin: 0.7rem 0 0.3rem 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0.6rem 0 0.25rem 0;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0.5rem 0 0.2rem 0;
  }
`;

const ProductPrice = styled.div`
  color: #e97d7dff;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.6rem;
  }
`;

const AddToCartButton = styled.button`
  background: #e97d7d;
  color: #fff;
  border: none;
  border-radius: 1rem;
  padding: 0.5rem 1rem;
  margin: 0.5rem 0;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #d96a6a;
    transform: translateY(-1px);
  }

  @media (max-width: 1024px) {
    display: none !important;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  animation: float 3s ease-in-out infinite;
  position: relative;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin-bottom: 1rem;
  }
`;

const Section = styled.section`
  padding: 3rem 0;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0;
  }
`;

const AnimatedCard = styled.div<{ isVisible: boolean; delay: number }>`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? "translateY(0) scale(1)" : "translateY(50px) scale(0.8)"};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(props) => props.delay}ms;
`;

const SkeletonCard = styled.div`
  background: #f4efef;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(233, 125, 125, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const SkeletonImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 1rem;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 160px;
  }
`;

const SkeletonText = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 0.5rem;
  border-radius: 4px;
`;

const SkeletonPrice = styled.div`
  width: 60%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-bottom: 1rem;
  border-radius: 4px;
`;

const SkeletonButton = styled.div`
  width: 100px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 16px;
  margin-bottom: 1rem;
`;

const FeaturedProducts: React.FC = () => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { addToCart } = useCartStore();
  const navigate = useNavigate();
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: false,
  });
  // Track touch positions to distinguish scrolls from taps
  const touchStartPositions = React.useRef<{
    [key: string]: { x: number; y: number };
  }>({});
  const isScrolling = React.useRef<{ [key: string]: boolean }>({});

  const {
    data: featuredProducts = [],
    isLoading,
    error,
  } = useFeaturedProducts();

  useEffect(() => {
    setShouldAnimate(false);
  }, []);

  useEffect(() => {
    if (!isLoading && featuredProducts.length > 0) {
      setTimeout(() => {
        setShouldAnimate(true);
      }, 100);
    }
  }, [isLoading, featuredProducts.length]);

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      qty: 1,
      images: product.images,
    });
  };

  const handleCardClick = (product: Product) => {
    navigate("/shop", {
      state: {
        selectedProductId: product.id,
        fromFeatured: true,
      },
    });
  };

  if (isLoading) {
    return (
      <Section ref={sectionRef}>
        <Container>
          <SectionTitle>Featured Products</SectionTitle>
          <Grid>
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonCard key={index}>
                <SkeletonImage />
                <SkeletonText />
                <SkeletonPrice />
                <SkeletonButton />
              </SkeletonCard>
            ))}
          </Grid>
        </Container>
      </Section>
    );
  }

  if (error) {
    return (
      <Section ref={sectionRef}>
        <Container>
          <SectionTitle>Featured Products</SectionTitle>
          <div
            style={{ textAlign: "center", color: "#e97d7d", padding: "2rem 0" }}
          >
            Failed to load products. Please try again later.
          </div>
        </Container>
      </Section>
    );
  }

  if (featuredProducts.length === 0) {
    return (
      <Section ref={sectionRef}>
        <Container>
          <SectionTitle>Featured Products</SectionTitle>
          <div
            style={{ textAlign: "center", color: "#666", padding: "2rem 0" }}
          >
            No featured products available at the moment. Please check back
            later!
          </div>
        </Container>
      </Section>
    );
  }

  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionTitle>Featured Products</SectionTitle>
        <Grid>
          {featuredProducts.map((product: Product, index: number) => {
            return (
              <AnimatedCard
                key={product.id}
                isVisible={shouldAnimate}
                delay={shouldAnimate ? index * 150 : 0}
              >
                <Card
                  onClick={() => handleCardClick(product)}
                  onTouchStart={(e) => {
                    // Record initial touch position
                    const touch = e.touches[0];
                    touchStartPositions.current[product.id] = {
                      x: touch.clientX,
                      y: touch.clientY,
                    };
                    isScrolling.current[product.id] = false;
                  }}
                  onTouchMove={(e) => {
                    // Detect if user is scrolling
                    const touch = e.touches[0];
                    const startPos = touchStartPositions.current[product.id];
                    if (startPos) {
                      const deltaX = Math.abs(touch.clientX - startPos.x);
                      const deltaY = Math.abs(touch.clientY - startPos.y);
                      // If movement is more than 10px, consider it a scroll
                      if (deltaX > 10 || deltaY > 10) {
                        isScrolling.current[product.id] = true;
                      }
                    }
                  }}
                  onTouchEnd={(e) => {
                    // Only trigger click if it wasn't a scroll
                    if (!isScrolling.current[product.id]) {
                      handleCardClick(product);
                    }
                    // Clean up
                    delete touchStartPositions.current[product.id];
                    delete isScrolling.current[product.id];
                  }}
                >
                  <ProductImage
                    src={product.images[0] || "/category1.PNG"}
                    alt={product.name}
                  />
                  <ProductTitle>{product.name}</ProductTitle>
                  <ProductPrice>${product.price.toFixed(2)}</ProductPrice>
                  <AddToCartButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                  >
                    Add to Cart
                  </AddToCartButton>
                </Card>
              </AnimatedCard>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default FeaturedProducts;
